import { clamp } from '@/core/rng';
import type { AuraState } from './aura';
import type { EventBus } from './events';

/**
 * The Divine Anchor (v0.3.2, recommended sequence item 8) — the First Sign
 * and the Totem merged into one thing.
 *
 * An Anchor is a bond between a deity and a *place*, and it is the only act
 * in the game that costs the god its own body: you cannot call one down from
 * the sky, you have to come and stand there. It sits outside the verb palette
 * for that reason. Everything else a god does is a gesture at a distance;
 * this is attendance.
 *
 * What it does NOT do is grant anybody. There are no followers here, no
 * conversion radius, no claim. Mortals have to find it on their own, come
 * back on their own, and decide on their own that returning is worth doing —
 * and the *place* is what they build up, footpath through temple, entirely
 * with their own hands. A god who places an Anchor where nobody walks has
 * spent a great deal on a stone nobody will ever see.
 *
 * Two quantities that must never be confused:
 *   RESONANCE is deity↔place. It is what the god actually made, it decays
 *     over centuries rather than seasons, and no mortal can spend it.
 *   REGARD (in the band's own record) is people↔place. That is faith's
 *     ancestor, it belongs to the mortals, and it can be lost.
 *
 * And two things it deliberately is not: a territory flag, and a building.
 * Rival gods act freely here — a contested Anchor is a feature, not a bug —
 * and if the object mortals raised over it is thrown down, the resonance is
 * untouched. Places outlive what is built on them.
 */

/**
 * What mortals have raised over the place, purely by going back. Each rung
 * is earned by visits, never placed by anybody, and the ladder is the same
 * shape as the rest of the world's history: repetition becomes structure.
 */
export type SacredStage =
  | 'bare' | 'footpath' | 'offerings' | 'cairn' | 'shelter' | 'shrine' | 'temple';

export const SACRED_LADDER: readonly SacredStage[] =
  ['bare', 'footpath', 'offerings', 'cairn', 'shelter', 'shrine', 'temple'];

/**
 * Visits needed to reach each rung. Steeply rising: a footpath is three trips
 * and a temple is the work of generations, which is what stops a keen band
 * from speed-running a cathedral in a season.
 */
const RUNG_VISITS = [0, 3, 8, 18, 40, 90, 200];

export interface AnchorState {
  id: string;
  /**
   * Whose hand placed it. World bookkeeping only — mortals never learn it,
   * and neither do rival gods; see perceivedCause in events.ts.
   */
  godId: string;
  x: number;
  z: number;
  placedAt: number;
  /** 0..1 — deity↔place. Decays over centuries; mortals cannot touch it. */
  resonance: number;
  /** What mortals have built here, by coming back. */
  stage: SacredStage;
  /** Total visits by anybody, ever. The ladder reads this. */
  visits: number;
  /** World-seconds of the most recent visit, if there has been one. */
  lastVisit?: number;
  /** Bands that know this place exists at all. */
  knownBy: string[];
  /**
   * Lit, if the god whose stone this is has left part of itself burning here
   * (`sim/aura.ts`, law 31). Optional and undefined by default, which is why
   * adding it cost no `SNAPSHOT_VERSION` bump: the anchor list serialises by
   * spread, so an old save simply restores every stone unlit.
   */
  aura?: AuraState;
}

/**
 * What it costs a god to make one, out of its `power`. Steep on purpose: it
 * was roughly a month of a god's capacity when power came back at 2% a day;
 * it is three of the five stones now (see `POWER_PER_DAY`), which is most of
 * what a god can hold and a quarter of an hour of regeneration to get back.
 * Rare, and felt.
 */
export const ANCHOR_COST = 0.6;
/**
 * Power a deity recovers per world-day.
 *
 * 0.02 until 2026-08-18 — fifty days from empty, priced for casts that cost
 * a thousandth of a god each. Griffin's direction that day made a cast cost a
 * STONE (`IMPULSE_STONE`, a fifth of the god; a tap spends one, a hold
 * charges up to all five), and a fifth of a god cannot come back at a fiftieth
 * a day or the god casts five times and waits an hour and a quarter (192×:
 * a world-day is 7.5 real minutes). 0.35 returns a stone every 0.57 world-days
 * — about four real minutes — which is 14 taps or three full charges an hour,
 * inside law 30's 8–25 meaningful casts per canonical hour. A design number,
 * written down to be measured against: `tools/economy.ts` prices in it, and
 * everything that was set against 0.02 is re-said at its reader
 * (`AURA_UPKEEP_PER_DAY`, `EARTH_MARK_VOLUME`, `ANCHOR_COST` above).
 */
export const POWER_PER_DAY = 0.35;
/**
 * How close a god must physically be. Not a casting range — a standing-here
 * range. You must fly down and be present at the spot.
 */
export const ANCHOR_REACH = 45;
/** And how low: an Anchor is made from the ground, not from the sky. */
export const ANCHOR_CEILING = 60;
/** How near a mortal must pass to notice one at all. */
export const NOTICE_RANGE = 130;
/**
 * Resonance half-life: two hundred world-years. "Decays very slowly" in the
 * design means a place a god attended is still that place long after every
 * mortal who saw it happen is dead.
 */
const RESONANCE_HALFLIFE = 200 * 365 * 86400;

/** Which rung this many visits has earned. */
export const stageForVisits = (visits: number): SacredStage => {
  let s: SacredStage = 'bare';
  for (let i = 0; i < SACRED_LADDER.length; i++) {
    if (visits >= RUNG_VISITS[i]) s = SACRED_LADDER[i];
  }
  return s;
};

export interface AnchorSnapshot {
  anchors: AnchorState[];
  nextId: number;
}

/**
 * All the mortal layer is allowed to know about anchored places: what is
 * near enough to walk into, and the two things feet can do about it. Narrow
 * on purpose — bands must never be able to place one, price one, or read a
 * god's resonance — and a world with no gods in it supplies the empty one
 * below and behaves exactly as it always did.
 */
export interface AnchorReader {
  all(): readonly AnchorState[];
  near(x: number, z: number, radius: number): AnchorState[];
  discover(a: AnchorState, bandId: string, now: number): boolean;
  visit(a: AnchorState, bandId: string, now: number): SacredStage | null;
}

export const NO_ANCHORS: AnchorReader = {
  all: () => [],
  near: () => [],
  discover: () => false,
  visit: () => null,
};

export class AnchorSystem {
  private readonly list: AnchorState[] = [];
  private nextId = 0;

  constructor(
    private readonly events: EventBus,
    /**
     * What a god's Godhood does to its own places. Injected the way
     * `AuraSystem`'s is, and for the same two reasons: nothing under
     * `src/sim/` may depend on `godhood/`, and a rival god in the same world
     * has to get its own answer. Defaults to 1, so every existing caller is
     * unaffected.
     *
     * It lives HERE rather than being passed in at each call because the
     * caller that matters is `mortals/band.ts`, and `test/deity.ts` scans
     * every file under `sim/mortals/` and fails if any of them so much as
     * names a `godId`. A band walks to a stone; it must not be able to know
     * whose stone it is.
     */
    private readonly mod: (godId: string, variable: string) => number = () => 1,
  ) {}

  all(): readonly AnchorState[] {
    return this.list;
  }

  /** Every anchor within `radius` of a point — the only spatial query. */
  near(x: number, z: number, radius: number): AnchorState[] {
    return this.list.filter((a) => Math.hypot(a.x - x, a.z - z) <= radius);
  }

  /**
   * Make one. The caller has already established that the god is standing
   * here and can afford it; this is the world writing it down.
   *
   * The event is emitted like any other, which means mortals near enough
   * will witness *something happening* — and, as with every divine act,
   * will have no idea what or whose. Being present at the making is not
   * the same as understanding it.
   */
  place(godId: string, x: number, z: number, now: number): AnchorState {
    const a: AnchorState = {
      id: `anchor-${this.nextId++}`,
      godId, x, z, placedAt: now,
      resonance: 1,
      stage: 'bare',
      visits: 0,
      knownBy: [],
    };
    this.list.push(a);
    this.events.emit({
      type: 'anchor_placed', time: now, x, z,
      radius: 90, magnitude: 1, salience: 0.85,
      cause: { kind: 'divine', godId },
    });
    return a;
  }

  /**
   * A band came to this place. Visits are what build the sacred landscape,
   * so this is the only way any rung is ever climbed — and the god has no
   * hand in it whatsoever.
   */
  visit(a: AnchorState, bandId: string, now: number): SacredStage | null {
    a.visits += 1;
    a.lastVisit = now;
    if (!a.knownBy.includes(bandId)) a.knownBy.push(bandId);
    /**
     * `anchor.strength` — what a god's places are worth to the people who
     * find them. It scales the LADDER READ and never `visits` itself, which
     * stays an honest count of how many times somebody actually came: a
     * stronger anchor climbs on fewer real visits, and the world's own record
     * of who came and how often is untouched.
     *
     * The god has no hand in the visiting — that is what the docstring above
     * says and it stays true. What a god can buy is how much each visit
     * *means* at a place it bound.
     */
    const next = stageForVisits(a.visits * this.mod(a.godId, 'anchor.strength'));
    if (next === a.stage) return null;
    a.stage = next;
    this.events.emit({
      type: 'sacred_raised', time: now, x: a.x, z: a.z,
      radius: 200, magnitude: 1, salience: 0.5,
      cause: { kind: 'mortal', bandId },
      data: { stage: next },
    });
    return next;
  }

  /** First sight of the place by a band that had never seen it. */
  discover(a: AnchorState, bandId: string, now: number): boolean {
    if (a.knownBy.includes(bandId)) return false;
    a.knownBy.push(bandId);
    this.events.emit({
      type: 'anchor_found', time: now, x: a.x, z: a.z,
      radius: 150, magnitude: 1, salience: 0.7,
      cause: { kind: 'mortal', bandId },
    });
    return true;
  }

  /**
   * The slow forgetting of the land itself. Resonance is the one quantity
   * here that only time can spend — no mortal act touches it, and throwing
   * down whatever was built over it does nothing at all.
   */
  decay(from: number, to: number): void {
    const dt = to - from;
    if (dt <= 0) return;
    for (const a of this.list) {
      /**
       * `resonance.decayRate` — the Root fantasy, and the one the ladder is
       * most obviously about: a god whose places hold on. The modifier scales
       * the RATE, so a value under 1 lengthens the half-life and a place a
       * patient god bound outlives one an impatient god bound.
       *
       * Per anchor rather than once for the whole list, because a world can
       * hold more than one god and they need not be equally patient — the same
       * reason every other seam here takes a `deityId`.
       */
      const rate = this.mod(a.godId, 'resonance.decayRate');
      const keep = Math.pow(0.5, (dt * rate) / RESONANCE_HALFLIFE);
      a.resonance = clamp(a.resonance * keep, 0, 1);
    }
  }

  toSnapshot(): AnchorSnapshot {
    return {
      nextId: this.nextId,
      anchors: this.list.map((a) => ({ ...a, knownBy: [...a.knownBy] })),
    };
  }

  restore(s: AnchorSnapshot): void {
    this.list.length = 0;
    for (const a of s.anchors) this.list.push({ ...a, knownBy: [...a.knownBy] });
    this.nextId = s.nextId;
  }
}
